/**
 * Complete Phrases Data Index
 * 整合所有短语数据源
 */

import { Phrase, phraseCategories } from './phrases-types';
import { elementaryGrade3to4Phrases } from './phrases-data-part1';
import { adjectivePhrases } from './phrases-adjectives';
import { actionPhrases } from './phrases-actions';
import { requestResponsePhrases } from './phrases-requests';
import { timePlacePhrases } from './phrases-time-place';
import { prepositionPhrases } from './phrases-prepositions';
import { modalQuantityPhrases } from './phrases-modal-quantity';
import { weatherHealthPhrases } from './phrases-weather-health';
import { grade5to6AdvancedPhrases } from './phrases-grade5-6';
import { elementaryExtendedPhrases } from './phrases-elementary-extended';
import { middleSchoolGrade7Phrases } from './phrases-middle-school-7';
import { middleSchoolGrade8Phrases } from './phrases-middle-school-8';
import { additionalPhrasesRound1 } from './phrases-additional-round1';
import { additionalPhrasesRound2 } from './phrases-additional-round2';

// 导出类型和分类
export type { Phrase };
export { phraseCategories };

// 合并所有短语数据
export const allPhrasesData: Phrase[] = [
  ...elementaryGrade3to4Phrases,
  ...adjectivePhrases,
  ...actionPhrases,
  ...requestResponsePhrases,
  ...timePlacePhrases,
  ...prepositionPhrases,
  ...modalQuantityPhrases,
  ...weatherHealthPhrases,
  ...grade5to6AdvancedPhrases,
  ...elementaryExtendedPhrases,
  ...middleSchoolGrade7Phrases,
  ...middleSchoolGrade8Phrases,
  ...additionalPhrasesRound1,
  ...additionalPhrasesRound2,
];

// 导出统计信息
export const phrasesStats = {
  total: allPhrasesData.length,
  elementary: allPhrasesData.filter(p => p.level === 'elementary').length,
  middle: allPhrasesData.filter(p => p.level === 'middle').length,

  byGrade: {
    '3': allPhrasesData.filter(p => p.grade === '3').length,
    '4': allPhrasesData.filter(p => p.grade === '4').length,
    '5': allPhrasesData.filter(p => p.grade === '5').length,
    '6': allPhrasesData.filter(p => p.grade === '6').length,
    '7': allPhrasesData.filter(p => p.grade === '7').length,
    '8': allPhrasesData.filter(p => p.grade === '8').length,
    '9': allPhrasesData.filter(p => p.grade === '9').length,
  },

  byFrequency: {
    high: allPhrasesData.filter(p => p.frequency === 'high').length,
    medium: allPhrasesData.filter(p => p.frequency === 'medium').length,
    low: allPhrasesData.filter(p => p.frequency === 'low').length,
  },

  byCategory: {
    greetings: allPhrasesData.filter(p => p.category === 'greetings').length,
    politeness: allPhrasesData.filter(p => p.category === 'politeness').length,
    daily_expressions: allPhrasesData.filter(p => p.category === 'daily_expressions').length,
    asking: allPhrasesData.filter(p => p.category === 'asking').length,
    requests: allPhrasesData.filter(p => p.category === 'requests').length,
    responses: allPhrasesData.filter(p => p.category === 'responses').length,
    time_place: allPhrasesData.filter(p => p.category === 'time_place').length,
    actions: allPhrasesData.filter(p => p.category === 'actions').length,
    feelings: allPhrasesData.filter(p => p.category === 'feelings').length,
    opinions: allPhrasesData.filter(p => p.category === 'opinions').length,
    academic: allPhrasesData.filter(p => p.category === 'academic').length,
    social: allPhrasesData.filter(p => p.category === 'social').length,
  },

  byDifficulty: {
    easy: allPhrasesData.filter(p => p.difficulty === 'easy').length,
    medium: allPhrasesData.filter(p => p.difficulty === 'medium').length,
    hard: allPhrasesData.filter(p => p.difficulty === 'hard').length,
  }
};

// Helper functions
export function getPhrasesByCategory(category: string): Phrase[] {
  return allPhrasesData.filter(phrase => phrase.category === category);
}

export function getPhrasesByDifficulty(difficulty: 'easy' | 'medium' | 'hard'): Phrase[] {
  return allPhrasesData.filter(phrase => phrase.difficulty === difficulty);
}

export function getPhrasesByLevel(level: 'elementary' | 'middle'): Phrase[] {
  return allPhrasesData.filter(phrase => phrase.level === level);
}

export function getPhrasesByGrade(grade: '3' | '4' | '5' | '6' | '7' | '8' | '9'): Phrase[] {
  return allPhrasesData.filter(phrase => phrase.grade === grade);
}

export function getRandomPhrases(count: number): Phrase[] {
  const shuffled = [...allPhrasesData].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

export function getPhraseById(id: string): Phrase | undefined {
  return allPhrasesData.find(phrase => phrase.id === id);
}

export function getHighFrequencyPhrases(): Phrase[] {
  return allPhrasesData.filter(phrase => phrase.frequency === 'high');
}

export function searchPhrases(keyword: string): Phrase[] {
  const lowerKeyword = keyword.toLowerCase();
  return allPhrasesData.filter(phrase =>
    phrase.phrase.toLowerCase().includes(lowerKeyword) ||
    phrase.meaning.includes(keyword) ||
    phrase.usageScenario.includes(keyword)
  );
}